import React, { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import { getBrandOpsContract, signBrandOpsContract } from '../../../redux/brandOps/actions';
import {
    brandOpsActiveContractSelector,
    brandOpsFetchingContractSelector,
    brandOpsMutatingContractSelector,
} from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { BRAND_LUXE_MUTED, BRAND_SURFACE_CONTAINER_HIGH, brandModalOutlinedInputSx } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    ManageSurface,
    MANAGE_PAGE_SX,
    useBrandManageGate,
} from './manageShared';

export default function BrandManageContractSignPage() {
    const { contractId = '' } = useParams();
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const nav = useNavigate();
    const { brandId } = useBrandManageGate();
    const contract = useSelector(brandOpsActiveContractSelector);
    const fetching = useSelector(brandOpsFetchingContractSelector);
    const mutating = useSelector(brandOpsMutatingContractSelector);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawing = useRef(false);
    const [typedName, setTypedName] = useState('');
    const [affirmed, setAffirmed] = useState(false);

    useEffect(() => {
        if (brandId && contractId) dispatch(getBrandOpsContract({ brandId, contractId }));
    }, [dispatch, brandId, contractId]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.strokeStyle = '#ffc965';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
    }, [contract]);

    const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        drawing.current = true;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const { x, y } = pos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const { x, y } = pos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const onPointerUp = () => {
        drawing.current = false;
    };

    const clearPad = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const sign = async () => {
        if (!brandId || !contractId) return;
        if (!typedName.trim() || !affirmed) {
            showToastMessage('Typed name and affirmation are required', 'error');
            return;
        }
        const dataUrl = canvasRef.current?.toDataURL('image/png') || '';
        if (!dataUrl || dataUrl.length < 100) {
            showToastMessage('Please draw your signature', 'error');
            return;
        }
        try {
            await dispatchP(
                signBrandOpsContract({
                    brandId,
                    contractId,
                    typed_name: typedName.trim(),
                    signature_data_url: dataUrl,
                    affirmed: true,
                }),
            );
            showToastMessage('Contract signed', 'success');
            nav(ROUTES.BRAND_MANAGE_CONTRACT_DETAIL.replace(':contractId', contractId));
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not sign contract', 'error');
        }
    };

    return (
        <BrandManageGate requireEntitlement={false}>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="Sign contract"
                    subtitle={contract?.title || contract?.manager_email || 'Review terms and sign electronically.'}
                    back={ROUTES.BRAND_MANAGE_CONTRACTS}
                />
                {fetching && !contract ? (
                    <Typography sx={{ color: BRAND_LUXE_MUTED }}>Loading…</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' }, gap: 2 }}>
                        <ManageSurface>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', mb: 1, fontSize: 14 }}>
                                Terms
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    whiteSpace: 'pre-wrap',
                                    fontSize: 12,
                                    lineHeight: 1.55,
                                    color: BRAND_LUXE_MUTED,
                                    fontFamily: 'ui-monospace, Menlo, monospace',
                                    maxHeight: 360,
                                    overflow: 'auto',
                                }}>
                                {contract?.legal_preview || 'Terms will appear here when the contract loads.'}
                            </Box>
                        </ManageSurface>
                        <ManageSurface>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', mb: 1, fontSize: 14 }}>
                                Signature
                            </Typography>
                            <Box
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                    borderRadius: 2,
                                    border: '1px dashed rgba(255,201,101,0.35)',
                                    mb: 1,
                                    touchAction: 'none',
                                }}>
                                <canvas
                                    ref={canvasRef}
                                    width={400}
                                    height={160}
                                    style={{ width: '100%', height: 160, display: 'block', cursor: 'crosshair' }}
                                    onPointerDown={onPointerDown}
                                    onPointerMove={onPointerMove}
                                    onPointerUp={onPointerUp}
                                    onPointerLeave={onPointerUp}
                                />
                            </Box>
                            <ManageGoldButton
                                variant="text"
                                onClick={clearPad}
                                sx={{ mb: 1.5, bgcolor: 'transparent', color: BRAND_LUXE_MUTED }}>
                                Clear pad
                            </ManageGoldButton>
                            <TextField
                                fullWidth
                                size="small"
                                label="Typed full name"
                                value={typedName}
                                onChange={e => setTypedName(e.target.value)}
                                sx={{ ...brandModalOutlinedInputSx, mb: 1 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={affirmed}
                                        onChange={e => setAffirmed(e.target.checked)}
                                        sx={{ color: BRAND_LUXE_MUTED, '&.Mui-checked': { color: '#ffc965' } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: '#fff', fontSize: 12 }}>
                                        I affirm I am authorized to sign this agreement electronically.
                                    </Typography>
                                }
                            />
                            <ManageGoldButton sx={{ mt: 2 }} disabled={mutating} onClick={sign}>
                                {mutating ? 'Signing…' : 'Sign contract'}
                            </ManageGoldButton>
                        </ManageSurface>
                    </Box>
                )}
            </Box>
        </BrandManageGate>
    );
}
